import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'

import { SnackbarConfigTypes } from '../../../commons/utils/Types'
import { BillingDateConfig } from '../../../models/BillingDateConfig'
import {
    getBilingDateConfig,
    setBilingDateConfig,
} from '../../AppPermissionsAPI'
import BillingDateConfigView from './BillingDateConfigView'

export default function BillingDateConfigContainer() {
    const [snackbarConfig, setSnackBarConfig] = useState<SnackbarConfigTypes>({
        message: '',
        isVisible: false,
        status: 'success',
    })
    const [isLoadingConfig, setIsLoadingConfig] = useState<boolean>(true)
    const [isSavingConfig, setIsSavingConfig] = useState<boolean>(false)
    const [billingDateConfig, setBillingDateConfig] =
        useState<BillingDateConfig>({
            config: {},
        })

    useEffect(() => {
        loadConfig()
    }, [])

    async function loadConfig() {
        try {
            const { data } = await getBilingDateConfig()
            if (data) setBillingDateConfig(data)
        } catch (error) {
            setSnackBarConfig((prev) => ({
                ...prev,
                isVisible: true,
                message: `Erro ao buscar configuração de datas, por favor tente mais tarde!`,
                status: 'error',
            }))
        } finally {
            setIsLoadingConfig(false)
        }
    }

    const isDateRangeFullfiled = () => {
        return Object.values(billingDateConfig.config).some(
            ({ start, end }) => {
                if (!start && !end) return false
                return !start || !end
            }
        )
    }

    const handleSaveConfig = async () => {
        try {
            setIsSavingConfig(true)
            await setBilingDateConfig(billingDateConfig)
            setSnackBarConfig((prev) => ({
                ...prev,
                isVisible: true,
                message: `Configuração salva com sucesso.`,
                status: 'success',
            }))
        } catch (error) {
            setSnackBarConfig((prev) => ({
                ...prev,
                isVisible: true,
                message: `Ocorreu um erro ao tentar salvar a configuração.`,
                status: 'error',
            }))
        } finally {
            setIsSavingConfig(false)
        }
    }

    const handleCloseSnackbar = () => {
        setSnackBarConfig((prev) => ({ ...prev, isVisible: false }))
    }

    const getDateByMonthRef = (ref: string) => {
        return ref === 'current' ? dayjs() : dayjs().add(1, 'M')
    }

    const handleCustomConfig = ({
        period,
        value,
        monthRef,
    }: HandleConfigDay) => {
        // TODO: Alterar para useMemo ou alguma outra forma que vai fazer a atualização da billingDate somente quando for salvar
        const dateRef = value
            ? getDateRef(value)
            : getDateRef(getDateByMonthRef(monthRef))
        if (!dateRef) return
        setBillingDateConfig((crr) => {
            const updatedConfig = { ...crr.config }
            const newConfig = {
                ...updatedConfig[dateRef],
                [period]: value?.date(),
            }

            if (dateRef.length < 5) {
                delete updatedConfig[dateRef]
                return crr
            }

            if (Object.values(newConfig).every((val) => val === undefined)) {
                delete updatedConfig[dateRef]
            } else {
                updatedConfig[dateRef] = newConfig
            }

            return { ...crr, config: updatedConfig }
        })
    }

    const handleDefaultConfig = ({ period, value }: HandleDefaultConfig) => {
        setBillingDateConfig((crr) => {
            const configToUpdate = crr.config.default
            const newConfig = { ...configToUpdate, [period]: value }
            return {
                ...crr,
                config: { ...crr.config, default: newConfig },
            }
        })
    }

    const getDateRef = (date: Dayjs): string => {
        if (!dayjs(date).isValid()) return ''
        const month = (date.month() + 1).toString()
        const year = date?.year().toString()
        return month + year
    }

    const makeDateFromRef = (date: Date, period: Period) => {
        const dateRef = getDateRef(dayjs(date))
        const existentConfig = billingDateConfig.config[dateRef]
        if (!existentConfig) return null
        const oneDigitMonthRef = dateRef.length === 5
        const month = Number(dateRef.slice(0, oneDigitMonthRef ? 1 : 2)) - 1
        const year = dateRef.slice(oneDigitMonthRef ? 1 : 2)
        const day = existentConfig[period]

        const newDate = new Date(Number(year), month, day)
        return newDate
    }

    const makeDaysOptions = () => {
        return new Array(31).fill(undefined).map((_, i) => {
            return { label: (i + 1).toString(), value: (i + 1).toString() }
        })
    }

    return (
        <BillingDateConfigView
            billingDateConfig={billingDateConfig}
            handleSaveConfig={handleSaveConfig}
            handleCloseSnackbar={handleCloseSnackbar}
            snackbarConfig={snackbarConfig}
            handleCustomConfig={handleCustomConfig}
            isLoadingConfig={isLoadingConfig}
            isSavingConfig={isSavingConfig}
            makeDateFromRef={makeDateFromRef}
            makeDaysOptions={makeDaysOptions}
            handleDefaultConfig={handleDefaultConfig}
            isDateRangeFullfiled={isDateRangeFullfiled}
        />
    )
}

export type HandleConfigDay = {
    period: Period
    value: Dayjs
    monthRef: 'current' | 'next'
}
export type HandleDefaultConfig = {
    period: Period
    value: number | null
}

export type Period = 'start' | 'end'
